import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApiData } from '../../contexts/apiService';
import { useDataManager } from '../../contexts/dataManager';
import { getHoustonDateInfo, getUniqueDropdownOptions } from '../../helper_functions';
import { orderTestTypes, buildTestTypeDefMap, labelForTestType, warningText } from './labResultsHelpers';

type Confidence = 'high' | 'medium' | 'low' | 'none' | undefined;

interface EntitySuggestion {
    id: number | null;
    name: string | null;
    confidence?: Confidence;
}

interface PotDraft {
    clientId: string;
    test_date: string;
    entity_type: 'lease' | 'market';
    lease_id: number | null;
    market_id: number | null;
    station_owner: string;
    entity_suggestion: EntitySuggestion | null;
    sample_point: string;
    sampled_by: string;
    conclusion: string;
    measurements: Record<string, string>;
    methods: Record<string, string>;
    note: string;
    lab_name: string;
    warnings?: string[];
}

interface LabPdfReviewModalProps {
    extractionResult: any;
    // Original uploaded PDF, shown side-by-side so the user can check the extracted
    // numbers against the source. Null for manual entry.
    pdfFile?: File | null;
    onClose: () => void;
    onCommitted: () => void;
    // When true and there are no extracted pots, start with one blank sample so
    // the user can type everything by hand (fallback when the PDF can't be read).
    startBlank?: boolean;
    // Entity type used for newly added blank samples (mirrors the page toggle).
    defaultEntityType?: 'lease' | 'market';
}

const uid = () => `p_${Math.random().toString(36).slice(2, 10)}`;

const blankPot = (entityType: 'lease' | 'market' = 'lease', testDate = ''): PotDraft => ({
    clientId: uid(),
    test_date: testDate,
    entity_type: entityType,
    lease_id: null,
    market_id: null,
    station_owner: '',
    entity_suggestion: null,
    sample_point: '',
    sampled_by: '',
    conclusion: '',
    measurements: {},
    methods: {},
    note: '',
    lab_name: '',
    warnings: undefined,
});

const confidenceIcon = (c?: Confidence) => {
    if (c === 'high') return { icon: '✓', cls: 'labPdf-conf--high', title: 'High confidence match' };
    if (c === 'medium' || c === 'low')
        return { icon: '⚠️', cls: 'labPdf-conf--warn', title: 'Low/medium confidence — verify' };
    return { icon: '❌', cls: 'labPdf-conf--none', title: 'No match detected — pick manually' };
};

const hydratePot = (raw: any, marketCoords: any[] = []): PotDraft => {
    const entity_type: 'lease' | 'market' =
        raw.entity_type === 'market' ? 'market' :
            raw.market_id ? 'market' : 'lease';

    const suggestion: EntitySuggestion | null = raw.entity_suggestion
        ? {
            id: raw.entity_suggestion.id ?? null,
            name: raw.entity_suggestion.name ?? null,
            confidence: raw.entity_suggestion.confidence,
        }
        : null;

    const numericMeas: Record<string, any> = raw.measurements || {};
    const textMeas: Record<string, any> = raw.measurements_text || {};
    const measurements: Record<string, string> = {};
    Object.entries(numericMeas).forEach(([tt, v]) => {
        if (v != null && v !== '') measurements[tt] = String(v);
    });
    Object.entries(textMeas).forEach(([tt, v]) => {
        if (v != null && v !== '') measurements[tt] = String(v);
    });

    const resolvedMarketId =
        entity_type === 'market' ? (raw.market_id ?? suggestion?.id ?? null) : null;

    let station_owner = raw.station_owner || '';
    if (entity_type === 'market' && !station_owner && resolvedMarketId != null) {
        // Owner comes straight off the matched market_coordinates row (its `market`
        // column), mirroring how AccTables models station owner.
        const coord = marketCoords.find((m: any) => Number(m.id) === Number(resolvedMarketId));
        if (coord?.market) station_owner = String(coord.market);
    }

    return {
        clientId: uid(),
        test_date: raw.test_date || '',
        entity_type,
        lease_id: entity_type === 'lease' ? (raw.lease_id ?? suggestion?.id ?? null) : null,
        market_id: resolvedMarketId,
        station_owner,
        entity_suggestion: suggestion,
        sample_point: raw.sample_point || '',
        sampled_by: raw.sampled_by || '',
        conclusion: raw.conclusion || measurements['conclusion'] || '',
        measurements,
        methods: raw.methods_per_test || {},
        note: raw.note || '',
        lab_name: raw.lab_name || '',
        warnings: Array.isArray(raw.warnings) ? raw.warnings : undefined,
    };
};

const LabPdfReviewModal: React.FC<LabPdfReviewModalProps> = ({
    extractionResult,
    pdfFile = null,
    onClose,
    onCommitted,
    startBlank = false,
    defaultEntityType = 'lease',
}) => {
    const { apiData, handleCommitLabPdf, handleGetTable } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const todayString = getHoustonDateInfo().todayString;

    // The modal reads these straight off apiData (lease/market dropdowns, the
    // test-type catalog and the lab list). Don't assume the parent loaded them —
    // request any that aren't cached yet (requestTableData dedups, so this is a
    // no-op when the Lab Results tab already fetched them).
    useEffect(() => {
        ['lease_coordinates', 'market_coordinates', 'test_type_definitions', 'lab_results'].forEach((t) => {
            if (!hasTableData(t)) requestTableData(t);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Test-type catalog → curated labels for the measurement rows and the
    // "+ Add test…" picker
    const testTypeDefs = useMemo(() => buildTestTypeDefMap(apiData?.test_type_definitions), [apiData?.test_type_definitions]);
    const allTestTypes = useMemo(
        () => (apiData?.test_type_definitions || []).map((d) => ({ value: d.test_type, label: labelForTestType(d.test_type, testTypeDefs) })),
        [apiData?.test_type_definitions, testTypeDefs],
    );

    // Object URL for the side-by-side PDF preview; revoked on unmount / file change.
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [showPdf, setShowPdf] = useState<boolean>(true);
    useEffect(() => {
        if (!pdfFile) { setPdfUrl(null); return; }
        const url = URL.createObjectURL(pdfFile);
        setPdfUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [pdfFile]);

    const extractedPots: any[] = useMemo(
        () => extractionResult?.pots || extractionResult?.data?.pots || [],
        [extractionResult]
    );
    // Warnings may be strings or backend objects ({ page, type, text_preview, … });
    // kept raw here and turned into display text at render via warningText().
    const topWarnings: any[] = useMemo(() => {
        const ws = extractionResult?.warnings || extractionResult?.data?.warnings || [];
        return Array.isArray(ws) ? ws : [];
    }, [extractionResult]);

    const [pots, setPots] = useState<PotDraft[]>(() => {
        if (extractedPots.length > 0) {
            return extractedPots.map((r) => hydratePot(r, apiData?.market_coordinates || []));
        }
        return startBlank ? [blankPot(defaultEntityType, todayString)] : [];
    });
    const [generalNote, setGeneralNote] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
    const [globalError, setGlobalError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Lab selector: options come from labs already used in the data, plus "Other"
    const labOptions = useMemo(() => {
        const rows = apiData?.lab_results || [];
        const labs = Array.from(new Set(rows.map((r: any) => r.lab_name).filter(Boolean)));
        return labs.sort((a: any, b: any) => String(a).localeCompare(String(b))).map((l: any) => String(l));
    }, [apiData?.lab_results]);

    // Selected lab; '__other__' means use the free-text field below.
    const [labChoice, setLabChoice] = useState<string>('');
    const [labOther, setLabOther] = useState<string>('');
    const [labError, setLabError] = useState<boolean>(false);
    const resolvedLab = labChoice === '__other__' ? labOther.trim() : labChoice;

    const leaseOptions = useMemo(
        () => getUniqueDropdownOptions({
            tableData: apiData?.lease_coordinates,
            valueColumn: 'id',
            labelColumn: 'lease',
        }),
        [apiData?.lease_coordinates]
    );

    const stationOwnerOptions = useMemo(
        () => getUniqueDropdownOptions({
            tableData: apiData?.market_coordinates,
            valueColumn: 'market',
            labelColumn: 'market',
        }),
        [apiData?.market_coordinates]
    );


    const getMarketsForOwner = (owner: string) =>
        getUniqueDropdownOptions({
            tableData: apiData?.market_coordinates,
            valueColumn: 'id',
            labelColumn: 'station',
            filterBy: { column: 'market', value: owner },
        });

    const allMarketOptions = useMemo(
        () => getUniqueDropdownOptions({
            tableData: apiData?.market_coordinates,
            valueColumn: 'id',
            labelColumn: 'station',
        }),
        [apiData?.market_coordinates]
    );

    const updatePot = (clientId: string, patch: Partial<PotDraft>) => {
        setPots((prev) => prev.map((p) => (p.clientId === clientId ? { ...p, ...patch } : p)));
    };


    const updatePotRecord = (clientId: string, field: 'measurements' | 'methods', tt: string, value: string) => {
        setPots((prev) =>
            prev.map((p) => {
                if (p.clientId !== clientId) return p;
                const next = { ...p[field] };
                if (value === '' || value == null) {
                    delete next[tt];
                } else {
                    next[tt] = value;
                }
                return { ...p, [field]: next };
            })
        );
    };

    const updateMeasurement = (clientId: string, tt: string, value: string) =>
        updatePotRecord(clientId, 'measurements', tt, value);

    const updateMethod = (clientId: string, tt: string, value: string) =>
        updatePotRecord(clientId, 'methods', tt, value);

    const addTestTypeToPot = (clientId: string, tt: string) => {
        setPots((prev) =>
            prev.map((p) => {
                if (p.clientId !== clientId) return p;
                if (p.measurements[tt] != null) return p;
                return { ...p, measurements: { ...p.measurements, [tt]: '' } };
            })
        );
    };

    // Remove a measurement row entirely (its value and its method).
    const removeTestFromPot = (clientId: string, tt: string) => {
        setPots((prev) =>
            prev.map((p) => {
                if (p.clientId !== clientId) return p;
                const measurements = { ...p.measurements };
                const methods = { ...p.methods };
                delete measurements[tt];
                delete methods[tt];
                return { ...p, measurements, methods };
            })
        );
    };

    const deletePot = (clientId: string) => {
        setPots((prev) => prev.filter((p) => p.clientId !== clientId));
    };

    const addPot = () => {
        setPots((prev) => [...prev, blankPot(defaultEntityType, todayString)]);
    };

    const toggleEntityType = (clientId: string, next: 'lease' | 'market') => {
        setPots((prev) =>
            prev.map((p) => {
                if (p.clientId !== clientId) return p;
                if (next === 'lease') {
                    return { ...p, entity_type: 'lease', market_id: null, station_owner: '' };
                }
                return { ...p, entity_type: 'market', lease_id: null };
            })
        );
    };

    const applyGeneralNote = () => {
        if (!generalNote.trim()) return;
        setPots((prev) => prev.map((p) => ({ ...p, note: generalNote })));
    };

    const validate = (): boolean => {
        const errors: Record<string, string[]> = {};
        pots.forEach((p) => {
            const e: string[] = [];
            if (!p.test_date) e.push('test_date');
            if (p.entity_type === 'lease' && !p.lease_id) e.push('lease_id');
            if (p.entity_type === 'market' && !p.market_id) e.push('market_id');
            const hasAny = Object.values(p.measurements).some((v) => v != null && String(v).trim() !== '');
            if (!hasAny && !p.conclusion.trim()) e.push('measurements');
            if (e.length) errors[p.clientId] = e;
        });
        setValidationErrors(errors);

        const firstBad = Object.keys(errors)[0];
        if (firstBad) {
            rowRefs.current[firstBad]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return Object.keys(errors).length === 0;
    };

    const buildPayload = () => {
        const serialized = pots.map((p) => {
            const measurementsArr: { test_type: string; result: number | null; result_text: string | null; test_method: string | null }[] = [];
            Object.entries(p.measurements).forEach(([tt, raw]) => {
                const v = String(raw).trim();
                if (!v) return;
                const test_method = p.methods?.[tt] ?? null;
                // Classify by parsing (same rule the backend applies on write):
                // numeric → result, anything else → result_text. No hardcoded list.
                const num = Number(v);
                if (!Number.isNaN(num)) {
                    measurementsArr.push({ test_type: tt, result: num, result_text: null, test_method });
                } else {
                    measurementsArr.push({ test_type: tt, result: null, result_text: v, test_method });
                }
            });
            if (p.conclusion.trim() && !p.measurements['conclusion']) {
                measurementsArr.push({ test_type: 'conclusion', result: null, result_text: p.conclusion.trim(), test_method: null });
            }
            return {
                test_date: p.test_date,
                lease_id: p.entity_type === 'lease' ? p.lease_id : null,
                market_id: p.entity_type === 'market' ? p.market_id : null,
                sample_point: p.sample_point || null,
                sampled_by: p.sampled_by || null,
                // Lab chosen at the modal level applies to every sample; fall back
                // to whatever the pot already carried (e.g. extracted from the PDF).
                lab_name: resolvedLab || p.lab_name || null,
                note: p.note || null,
                measurements: measurementsArr,
            };
        });
        return { pots: serialized };
    };

    const onCommit = async () => {
        if (submitting) return;
        setGlobalError(null);
        setSuccessMessage(null);
        if (pots.length === 0) {
            setGlobalError('There are no samples to save.');
            return;
        }
        // Lab is required — it must not silently default to anything.
        if (!resolvedLab) {
            setLabError(true);
            setGlobalError('Please select a Lab before saving.');
            return;
        }
        if (!validate()) return;

        setSubmitting(true);
        try {
            const res = await handleCommitLabPdf(buildPayload());
            if (!res || res.success === false) {
                setGlobalError(res?.message || 'Could not save the lab report.');
                setSubmitting(false);
                return;
            }
            const insertedCount = res.inserted_rows ?? res.inserted ?? 0;
            const skipped = res.skipped_duplicates ?? 0;
            let msg = insertedCount > 0
                ? `Saved ${insertedCount} result row(s) successfully.`
                : `No new results saved.`;
            if (skipped > 0) {
                msg += ` ${skipped} duplicate${skipped === 1 ? '' : 's'} already existed and ${skipped === 1 ? 'was' : 'were'} skipped.`;
            }
            setSuccessMessage(msg);
            handleGetTable('lab_results');
            // Leave the message up longer when nothing new was saved so the user
            // notices the duplicates were skipped before the modal closes.
            setTimeout(() => onCommitted(), insertedCount > 0 ? 1500 : 2800);
        } catch (e: any) {
            setGlobalError(e?.message || 'Unexpected error while saving.');
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (extractedPots.length > 0) {
            setPots(extractedPots.map((r) => hydratePot(r, apiData?.market_coordinates || [])));
        } else if (startBlank) {
            // Keep any rows the user already started; only seed a blank one if empty.
            setPots((prev) => (prev.length ? prev : [blankPot(defaultEntityType, todayString)]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extractedPots, apiData?.market_coordinates]);


    useEffect(() => {
        if (labChoice) return;
        const fromPots = extractedPots.map((r: any) => r.lab_name).find(Boolean);
        if (fromPots && labOptions.includes(String(fromPots))) {
            setLabChoice(String(fromPots));
        } else if (labOptions.length === 1) {
            setLabChoice(labOptions[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labOptions, extractedPots]);

    const renderEntityCell = (p: PotDraft) => {
        const conf = confidenceIcon(p.entity_suggestion?.confidence);
        const bad = validationErrors[p.clientId]?.includes(p.entity_type === 'lease' ? 'lease_id' : 'market_id');
        if (p.entity_type === 'lease') {
            return (
                <div className={`labPdf-entity-cell ${bad ? 'labPdf-cell--bad' : ''}`}>
                    <select
                        className="labPdf-input"
                        value={p.lease_id ?? ''}
                        onChange={(e) => updatePot(p.clientId, { lease_id: e.target.value ? Number(e.target.value) : null })}
                    >
                        <option value="">— Select lease —</option>
                        {leaseOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    {p.entity_suggestion && (
                        <span className={`labPdf-conf ${conf.cls}`} title={conf.title}>{conf.icon}</span>
                    )}
                </div>
            );
        }
        const filtered = p.station_owner ? getMarketsForOwner(p.station_owner) : [];
        const stationOptions = filtered.length > 0 ? filtered : allMarketOptions;
        return (
            <div className={`labPdf-entity-cell labPdf-entity-cell--market ${bad ? 'labPdf-cell--bad' : ''}`}>
                <select
                    className="labPdf-input labPdf-input--narrow"
                    value={p.station_owner}
                    onChange={(e) => updatePot(p.clientId, {
                        station_owner: e.target.value,
                        market_id: null,
                    })}
                >
                    <option value="">— Station Owner —</option>
                    {stationOwnerOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                <select
                    className="labPdf-input"
                    value={p.market_id ?? ''}
                    onChange={(e) => updatePot(p.clientId, { market_id: e.target.value ? Number(e.target.value) : null })}
                >
                    <option value="">— Station / Market —</option>
                    {stationOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                {p.entity_suggestion && (
                    <span className={`labPdf-conf ${conf.cls}`} title={conf.title}>{conf.icon}</span>
                )}
            </div>
        );
    };

    return (
        <div className="carrier-modal-overlay">
            <div
                className={`carrier-modal-content labPdf-review-modal ${showPdf && pdfUrl ? 'labPdf-review-modal--pdf' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="labPdf-review-header">
                    <div>
                        <h2 className="labPdf-title">
                            {extractedPots.length === 0 ? 'Enter Lab Samples Manually' : 'Review Extracted Lab Samples'}
                        </h2>
                        <p className="labPdf-subtitle">
                            {extractedPots.length === 0
                                ? 'Add as many rows as you need. Rows are only written on Save.'
                                : 'One row per sample. Verify every highlighted field before saving. Rows are only written on Save.'}
                        </p>
                        <p className="labPdf-required-legend">
                            <span className="labPdf-required">*</span> Required: date, lease/market and lab.
                            Each sample also needs at least one result or a conclusion.
                        </p>
                    </div>
                    <div className="labPdf-header-actions">
                        {pdfUrl && (
                            <button
                                type="button"
                                className={`labPdf-btn labPdf-btn--ghost ${showPdf ? 'labPdf-btn--active' : ''}`}
                                onClick={() => setShowPdf((v) => !v)}
                                title="Show the original PDF next to the form to compare values"
                            >
                                {showPdf ? '📄 Hide PDF' : '📄 Show PDF'}
                            </button>
                        )}
                        <button
                            type="button"
                            className="labPdf-btn labPdf-btn--ghost labPdf-btn--close"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {topWarnings.length > 0 && (
                    <div className="labPdf-warning-banner">
                        <strong>Heads up:</strong>{' '}
                        {topWarnings.map((w, i) => (
                            <span key={i} className="labPdf-warning-chip">
                                {warningText(w)}
                            </span>
                        ))}
                    </div>
                )}

                <div className="labPdf-general-note-row">
                    <label className="labPdf-lab-field">
                        <span className="labPdf-lab-label">Lab:<span className="labPdf-lab-required">*</span></span>
                        <select
                            className={`labPdf-input labPdf-lab-select ${labError ? 'labPdf-input--bad' : ''}`}
                            value={labChoice}
                            onChange={(e) => { setLabChoice(e.target.value); setLabError(false); }}
                        >
                            <option value="">— Select lab —</option>
                            {labOptions.map((l) => (
                                <option key={l} value={l}>{l}</option>
                            ))}
                            <option value="__other__">Other…</option>
                        </select>
                        {labChoice === '__other__' && (
                            <input
                                type="text"
                                className={`labPdf-input labPdf-lab-other ${labError ? 'labPdf-input--bad' : ''}`}
                                placeholder="Lab name"
                                value={labOther}
                                onChange={(e) => { setLabOther(e.target.value); setLabError(false); }}
                            />
                        )}
                        {labError && <span className="labPdf-lab-error">Lab is required</span>}
                    </label>
                    <input
                        type="text"
                        className="labPdf-input labPdf-general-note"
                        placeholder="General note (optional) — can be applied to every sample"
                        value={generalNote}
                        onChange={(e) => setGeneralNote(e.target.value)}
                    />
                    <button
                        type="button"
                        className="labPdf-btn labPdf-btn--ghost"
                        onClick={applyGeneralNote}
                        disabled={!generalNote.trim()}
                    >
                        Apply to all
                    </button>
                </div>

                <div className={`labPdf-review-split ${showPdf && pdfUrl ? 'labPdf-review-split--pdf' : ''}`}>
                    {showPdf && pdfUrl && (
                        <div className="labPdf-pdf-pane">
                            {/* #navpanes=0 hides the viewer's thumbnail sidebar (single page). */}
                            <iframe title="Original lab PDF" src={`${pdfUrl}#navpanes=0`} className="labPdf-pdf-frame" />
                        </div>
                    )}
                    <div className="labPdf-cards-wrapper">
                        {pots.length === 0 && (
                            <div className="labPdf-empty">
                                No samples yet.{' '}
                                <button type="button" className="labPdf-btn labPdf-btn--ghost" onClick={addPot}>
                                    + Add Sample
                                </button>
                            </div>
                        )}
                        {pots.map((p, idx) => {
                            const errs = validationErrors[p.clientId] || [];
                            const potTests = orderTestTypes(new Set(Object.keys(p.measurements)), testTypeDefs);
                            return (
                                <div
                                    key={p.clientId}
                                    ref={(el) => { rowRefs.current[p.clientId] = el; }}
                                    className={`labPdf-card ${errs.length ? 'labPdf-card--bad' : ''}`}
                                >
                                    <div className="labPdf-card-head">
                                        <span className="labPdf-card-num">Sample {idx + 1}</span>
                                        <button
                                            type="button"
                                            className="labPdf-btn labPdf-btn--delete"
                                            onClick={() => deletePot(p.clientId)}
                                            title="Remove this sample"
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    <div className="labPdf-card-fields">
                                        <label className="labPdf-field">
                                            <span className="labPdf-field-label">Date<span className="labPdf-required">*</span></span>
                                            <input
                                                type="date"
                                                lang="en-US"
                                                className={`labPdf-input ${errs.includes('test_date') ? 'labPdf-input--bad' : ''}`}
                                                value={p.test_date}
                                                onChange={(e) => updatePot(p.clientId, { test_date: e.target.value })}
                                            />
                                        </label>
                                        <label className="labPdf-field">
                                            <span className="labPdf-field-label">Type</span>
                                            <div className="labPdf-type-toggle">
                                                <button
                                                    type="button"
                                                    className={`labPdf-type-btn ${p.entity_type === 'lease' ? 'labPdf-type-btn--active' : ''}`}
                                                    onClick={() => toggleEntityType(p.clientId, 'lease')}
                                                >
                                                    Lease
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`labPdf-type-btn ${p.entity_type === 'market' ? 'labPdf-type-btn--active' : ''}`}
                                                    onClick={() => toggleEntityType(p.clientId, 'market')}
                                                >
                                                    Market
                                                </button>
                                            </div>
                                        </label>
                                        <label className="labPdf-field labPdf-field--wide">
                                            <span className="labPdf-field-label">
                                                {p.entity_type === 'lease' ? 'Lease' : 'Market'}<span className="labPdf-required">*</span>
                                            </span>
                                            {renderEntityCell(p)}
                                        </label>
                                        <label className="labPdf-field">
                                            <span className="labPdf-field-label">Sample Point</span>
                                            <input
                                                type="text"
                                                className="labPdf-input"
                                                value={p.sample_point}
                                                onChange={(e) => updatePot(p.clientId, { sample_point: e.target.value })}
                                            />
                                        </label>
                                        <label className="labPdf-field">
                                            <span className="labPdf-field-label">Sampled By</span>
                                            <input
                                                type="text"
                                                className="labPdf-input"
                                                value={p.sampled_by}
                                                onChange={(e) => updatePot(p.clientId, { sampled_by: e.target.value })}
                                            />
                                        </label>
                                        <label className="labPdf-field labPdf-field--wide">
                                            <span className="labPdf-field-label">Conclusion</span>
                                            <input
                                                type="text"
                                                className={`labPdf-input ${errs.includes('measurements') ? 'labPdf-input--bad' : ''}`}
                                                value={p.conclusion}
                                                onChange={(e) => updatePot(p.clientId, { conclusion: e.target.value })}
                                                placeholder="Accept / Reject / …"
                                            />
                                        </label>
                                        <label className="labPdf-field labPdf-field--wide">
                                            <span className="labPdf-field-label">Note</span>
                                            <input
                                                type="text"
                                                className="labPdf-input"
                                                value={p.note}
                                                onChange={(e) => updatePot(p.clientId, { note: e.target.value })}
                                            />
                                        </label>
                                    </div>

                                    <div className="labPdf-measures">
                                        <div className="labPdf-measures-head">
                                            <span>Test</span>
                                            <span>Result</span>
                                            <span>Method</span>
                                            <span></span>
                                        </div>
                                        {potTests.length === 0 && (
                                            <div className={`labPdf-measures-empty ${errs.includes('measurements') ? 'labPdf-input--bad' : ''}`}>
                                                No measurements yet — add one below or fill the conclusion.
                                            </div>
                                        )}
                                        {potTests.map((tt) => {
                                            return (
                                                <div className="labPdf-measure-row" key={tt}>
                                                    <span className="labPdf-measure-name" title={labelForTestType(tt, testTypeDefs)}>
                                                        {labelForTestType(tt, testTypeDefs)}
                                                    </span>
                                                    {/* Free-text input: the value is classified numeric vs text on
                                                        save (and by the backend), so no per-test input type is needed. */}
                                                    <input
                                                        type="text"
                                                        className="labPdf-input"
                                                        value={p.measurements[tt] ?? ''}
                                                        onChange={(e) => updateMeasurement(p.clientId, tt, e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="labPdf-input labPdf-input--method"
                                                        placeholder="Method"
                                                        value={p.methods[tt] ?? ''}
                                                        onChange={(e) => updateMethod(p.clientId, tt, e.target.value)}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="labPdf-measure-remove"
                                                        title="Remove this test"
                                                        onClick={() => removeTestFromPot(p.clientId, tt)}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        <select
                                            className="labPdf-input labPdf-measure-add"
                                            value=""
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    addTestTypeToPot(p.clientId, e.target.value);
                                                    e.target.value = '';
                                                }
                                            }}
                                        >
                                            <option value="">+ Add test…</option>
                                            {allTestTypes
                                                .filter((t) => p.measurements[t.value] == null)
                                                .map((t) => (
                                                    <option key={t.value} value={t.value}>{t.label}</option>
                                                ))}
                                        </select>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {successMessage && <div className="labPdf-success">✓ {successMessage}</div>}
                {globalError && <div className="labPdf-error">{globalError}</div>}
                {Object.keys(validationErrors).length > 0 && (
                    <div className="labPdf-error">
                        Some samples are incomplete. Missing fields are highlighted — each sample needs a date,
                        a lease or market, and at least one measurement or a conclusion.
                    </div>
                )}

                <div className="labPdf-actions labPdf-actions--split">
                    <div className="labPdf-actions-left">
                        <button
                            type="button"
                            className="labPdf-btn labPdf-btn--ghost"
                            onClick={addPot}
                            disabled={submitting}
                        >
                            + Add Sample
                        </button>
                        <span className="labPdf-counter">
                            {pots.length} sample{pots.length === 1 ? '' : 's'} ready
                        </span>
                    </div>
                    <div>
                        <button
                            type="button"
                            className="labPdf-btn labPdf-btn--ghost"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="labPdf-btn labPdf-btn--primary"
                            onClick={onCommit}
                            disabled={submitting || pots.length === 0}
                        >
                            {submitting ? 'Saving…' : 'Save to Lab Results'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LabPdfReviewModal;
